import { Link } from 'react-router';
import type { ActivityFeedItem } from '@ridehub/shared';

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const diff = now - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  if (hours < 24) return `${hours}時間前`;
  return `${days}日前`;
}

function ActivityIcon({ type }: { type: ActivityFeedItem['type'] }) {
  if (type === 'checkin') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="w-4 h-4">
        <path
          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (type === 'spot') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="w-4 h-4">
        <path
          d="M12 2L2 7L12 12L22 7L12 2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2 17L12 22L22 17"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2 12L12 17L22 12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // photo
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="w-4 h-4">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
      <path
        d="M21 15L16 10L5 21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ACTIVITY_LABELS: Record<ActivityFeedItem['type'], string> = {
  checkin: 'チェックインしました',
  spot: 'スポットを登録しました',
  photo: '写真を投稿しました',
};

const ACCENT_BORDER: Record<ActivityFeedItem['type'], string> = {
  checkin: 'bg-blue-500',
  spot: 'bg-emerald-500',
  photo: 'bg-amber-500',
};

interface TimelineItemProps {
  item: ActivityFeedItem;
}

export function TimelineItem({ item }: TimelineItemProps) {
  const { user, spot, type, createdAt, photoUrl } = item;

  const initials = user.nickname.split('').slice(0, 2).join('').toUpperCase();

  return (
    <div className="flex bg-white border-b border-gray-100">
      {/* アクティビティ種別カラーの縦ボーダー */}
      <div className={`w-1 flex-shrink-0 ${ACCENT_BORDER[type]}`} />

      <div className="flex gap-3 px-3 py-3.5 flex-1 min-w-0">
        {/* アバター */}
        <Link to={`/users/${user.id}`} className="flex-shrink-0">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.nickname}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
              {initials}
            </div>
          )}
        </Link>

        {/* コンテンツ */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <Link
              to={`/users/${user.id}`}
              className="font-semibold text-gray-900 text-sm hover:underline"
            >
              {user.nickname}
            </Link>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <ActivityIcon type={type} />
              {ACTIVITY_LABELS[type]}
            </span>
            <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
              {formatRelativeTime(createdAt)}
            </span>
          </div>

          <Link
            to={`/spots/${spot.id}`}
            className="block text-sm text-primary font-medium hover:underline truncate"
          >
            {spot.name}
          </Link>

          {/* 写真サムネイル */}
          {type === 'photo' && photoUrl && (
            <Link to={`/spots/${spot.id}`}>
              <img
                src={photoUrl}
                alt={spot.name}
                className="mt-2 rounded-lg w-full max-w-xs h-32 object-cover"
              />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
